// src/features/task/detail/TaskSection.tsx
// Chrome for one part of a task page.
//
// Deliberately not a Card. Eight bordered cards down one column read as eight
// unrelated widgets; the parts of a task are not unrelated, they are one thing
// seen from several sides. A hairline and a quiet label separate them, which is
// enough to tell them apart and little enough to keep them a single page.

import type { ReactNode } from 'react';
import { cn } from '@core/ui';

interface TaskSectionProps {
  title: string;
  /** Buttons that belong to this part — "add", "log time", and the like. */
  action?: ReactNode;
  /** Small count or summary shown beside the title. */
  meta?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function TaskSection({ title, action, meta, className, children }: TaskSectionProps) {
  return (
    <section className={cn('border-t border-border pt-5', className)}>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
        {action && <div className="ml-auto flex items-center gap-1.5">{action}</div>}
      </div>
      {children}
    </section>
  );
}

/** The row that offers the parts this task does not have yet. */
export function SectionAddBar({
  items,
}: {
  items: { name: string; label: string; onAdd: () => void }[];
}) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-4">
      {items.map((item) => (
        <button
          key={item.name}
          type="button"
          onClick={item.onAdd}
          className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          {item.label}
        </button>
      ))}
    </div>
  );
}
